import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { groupApi, collectionApi, inviteApi, adminApi, organizationApi } from '../utils/api';
import { BASE_URL } from '../constants/global';
import type { Group, CreateGroupDTO, AddMemberDTO } from '../models/Group';
import type { Collection, CreateCollectionDTO } from '../models/Collection';
import type { GroupInvite, CreateInviteDTO } from '../models/Invite';

// ─── Helpers ───

function useToken() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  return { getAccessTokenSilently, isAuthenticated };
}

async function fetchJson(url: string, token: string, options?: RequestInit) {
  const response = await fetch(`${BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP error! status: ${response.status}`);
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null;
  }
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return null;
}

// ─── Group Queries ───

export function useMyGroups() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['groups', 'my'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return groupApi.getMyGroups(token);
    },
    enabled: isAuthenticated,
  });
}

export function usePublicGroups() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['groups', 'public'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return groupApi.getPublicGroups(token);
    },
    enabled: isAuthenticated,
  });
}

export function useAllGroups() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['groups', 'all'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return groupApi.getAllGroups(token);
    },
    enabled: isAuthenticated,
  });
}

export function useGroup(id: number) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['groups', id],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return groupApi.getById(id, token);
    },
    enabled: isAuthenticated && !!id,
  });
}

export function useGroupMembers(groupId: number) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['groups', groupId, 'members'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return groupApi.getMembers(groupId, token);
    },
    enabled: isAuthenticated && !!groupId,
  });
}

export function useGroupCollections(groupId: number) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['groups', groupId, 'collections'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return collectionApi.getByGroup(groupId, token);
    },
    enabled: isAuthenticated && !!groupId,
  });
}

export function usePendingGroupInvites(groupId: number) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['groups', groupId, 'invites', 'pending'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return inviteApi.getPendingGroupInvites(groupId, token);
    },
    enabled: isAuthenticated && !!groupId,
  });
}

// ─── Group Mutations ───

export function useCreateGroup() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (data: CreateGroupDTO) => {
      const token = await getAccessTokenSilently();
      return groupApi.create(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CreateGroupDTO }) => {
      const token = await getAccessTokenSilently();
      return groupApi.update(id, data, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['groups', 'my'] });
      queryClient.invalidateQueries({ queryKey: ['groups', 'all'] });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (id: number) => {
      const token = await getAccessTokenSilently();
      return groupApi.delete(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useJoinGroup() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (groupId: number) => {
      const token = await getAccessTokenSilently();
      return groupApi.joinPublicGroup(groupId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useLeaveGroup() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (groupId: number) => {
      const token = await getAccessTokenSilently();
      return groupApi.leaveGroup(groupId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useAddMember() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ groupId, data }: { groupId: number; data: AddMemberDTO }) => {
      const token = await getAccessTokenSilently();
      return groupApi.addMember(groupId, data, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups', variables.groupId, 'members'] });
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ groupId, userId, role }: { groupId: number; userId: number; role: string }) => {
      const token = await getAccessTokenSilently();
      return groupApi.updateMemberRole(groupId, userId, role, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups', variables.groupId, 'members'] });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: number; userId: number }) => {
      const token = await getAccessTokenSilently();
      return groupApi.removeMember(groupId, userId, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups', variables.groupId, 'members'] });
    },
  });
}

// ─── Collection Queries ───

export function useMyCollections() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['collections', 'my'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return collectionApi.getMyCollections(token);
    },
    enabled: isAuthenticated,
  });
}

export function useAccessibleCollections() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['collections', 'accessible'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return collectionApi.getAccessible(token);
    },
    enabled: isAuthenticated,
  });
}

export function useCollection(id: number) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['collections', id],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return collectionApi.getById(id, token);
    },
    enabled: isAuthenticated && !!id,
  });
}

export function useCollectionImageIds(collectionId: number) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['collections', collectionId, 'imageIds'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return collectionApi.getImageIds(collectionId, token);
    },
    enabled: isAuthenticated && !!collectionId,
  });
}

export function useCollectionImages(collectionId: number) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['collections', collectionId, 'images'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const ids = await collectionApi.getImageIds(collectionId, token);
      if (ids.length === 0) return [];
      return collectionApi.getImagesByIds(ids, token);
    },
    enabled: isAuthenticated && !!collectionId,
  });
}

export function useCollectionLabels() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['collections', 'labels'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return collectionApi.getLabels(token);
    },
    enabled: isAuthenticated,
  });
}

// ─── Collection Mutations ───

export function useCreateCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (data: CreateCollectionDTO) => {
      const token = await getAccessTokenSilently();
      return collectionApi.create(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
    },
  });
}

export function useUpdateCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CreateCollectionDTO }) => {
      const token = await getAccessTokenSilently();
      return collectionApi.update(id, data, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collections', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['collections', 'my'] });
      queryClient.invalidateQueries({ queryKey: ['collections', 'accessible'] });
    },
  });
}

export function useDeleteCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (id: number) => {
      const token = await getAccessTokenSilently();
      return collectionApi.delete(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] });
    },
  });
}

export function useAddImageToCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ collectionId, imageId }: { collectionId: number; imageId: number }) => {
      const token = await getAccessTokenSilently();
      return collectionApi.addImage(collectionId, imageId, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collections', variables.collectionId, 'imageIds'] });
      queryClient.invalidateQueries({ queryKey: ['collections', variables.collectionId, 'images'] });
    },
  });
}

export function useRemoveImageFromCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ collectionId, imageId }: { collectionId: number; imageId: number }) => {
      const token = await getAccessTokenSilently();
      return collectionApi.removeImage(collectionId, imageId, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collections', variables.collectionId, 'imageIds'] });
      queryClient.invalidateQueries({ queryKey: ['collections', variables.collectionId, 'images'] });
    },
  });
}

export function useAddImagesToCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ collectionId, imageIds }: { collectionId: number; imageIds: number[] }) => {
      const token = await getAccessTokenSilently();
      return collectionApi.addImages(collectionId, imageIds, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collections', variables.collectionId, 'imageIds'] });
      queryClient.invalidateQueries({ queryKey: ['collections', variables.collectionId, 'images'] });
    },
  });
}

export function useShareCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ collectionId, groupId }: { collectionId: number; groupId: number }) => {
      const token = await getAccessTokenSilently();
      return collectionApi.share(collectionId, groupId, token);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collections', variables.collectionId] });
      queryClient.invalidateQueries({ queryKey: ['collections', 'accessible'] });
    },
  });
}

export function useUnshareCollection() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (collectionId: number) => {
      const token = await getAccessTokenSilently();
      return collectionApi.unshare(collectionId, token);
    },
    onSuccess: (_, collectionId) => {
      queryClient.invalidateQueries({ queryKey: ['collections', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['collections', 'accessible'] });
    },
  });
}

// ─── Invite Queries ───

export function useMyPendingInvites() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['invites', 'my', 'pending'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return inviteApi.getMyPending(token);
    },
    enabled: isAuthenticated,
  });
}

// ─── Invite Mutations ───

export function useCreateInvite() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (data: CreateInviteDTO) => {
      const token = await getAccessTokenSilently();
      return inviteApi.create(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (inviteToken: string) => {
      const token = await getAccessTokenSilently();
      return inviteApi.accept(inviteToken, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useDeclineInvite() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (inviteToken: string) => {
      const token = await getAccessTokenSilently();
      return inviteApi.decline(inviteToken, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useCancelInvite() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async (inviteId: number) => {
      const token = await getAccessTokenSilently();
      return inviteApi.cancel(inviteId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

// ─── User Profile Queries ───

export function useUserProfile() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['user', 'profile'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return null;
      const text = await response.text();
      if (!text.trim()) return null;
      const data = JSON.parse(text);
      if (Object.keys(data).length === 0) return null;
      return data;
    },
    enabled: isAuthenticated,
  });
}

export function useUserImageCount(email: string | undefined) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['user', 'imageCount', email],
    queryFn: async () => {
      if (!email) return 0;
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${BASE_URL}/v1/image/user/${encodeURIComponent(email)}?page=0&size=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return 0;
      const data = await response.json();
      return data.page?.totalElements || 0;
    },
    enabled: isAuthenticated && !!email,
  });
}

// ─── Feature Flag Queries ───

export function useFeatureCheck(featureName: string) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['feature-toggles', 'check', featureName],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/feature-toggles/check/${featureName}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return { enabled: true };
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFeatureToggles() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['feature-toggles', 'all'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/feature-toggles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: isAuthenticated,
  });
}

// ─── Announcement Queries ───

export function useActiveAnnouncements() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/announcements/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return [];
      return response.json();
    },
    enabled: isAuthenticated,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useAllAnnouncements() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['announcements', 'all'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: isAuthenticated,
  });
}

// ─── Featured Collections Queries ───

export function useFeaturedCollections() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['featured-collections'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/featured-collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: isAuthenticated,
  });
}

// ─── Admin Queries ───

export function useAdminCheck() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'check'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.checkSuperAdmin(token);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminStats() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getStats(token);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminUsers() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getAllUsers(token);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminGroups() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'groups'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getAllGroups(token);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminErrorLogs(params: {
  page?: number;
  size?: number;
  errorCode?: string;
  errorType?: string;
  isResolved?: boolean;
  isIos?: boolean;
  userEmail?: string;
  startDateTs?: number;
  endDateTs?: number;
  isSuccess?: boolean;
  correlationId?: string;
}) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'errors', params],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getErrorLogs(token, params);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminErrorStats() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'errors', 'stats'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getErrorStats(token);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminTopErrorUsers(limit = 10) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'errors', 'top-users', limit],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getTopErrorUsers(token, limit);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminErrorHeatmap(days = 30) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'errors', 'heatmap', days],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getErrorHeatmap(token, days);
    },
    enabled: isAuthenticated,
  });
}

export function useAdminErrorRate() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['admin', 'errors', 'rate'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return adminApi.getErrorRate(token);
    },
    enabled: isAuthenticated,
  });
}

// ─── Admin Mutations ───

export function useToggleSuperAdmin() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ userId, isSuperAdmin }: { userId: number; isSuperAdmin: boolean }) => {
      const token = await getAccessTokenSilently();
      return adminApi.toggleSuperAdmin(userId, isSuperAdmin, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useAdminUpdateUser() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ userId, data }: { userId: number; data: any }) => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/user/admin/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update user');
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function usePurgeUserData() {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useToken();
  return useMutation({
    mutationFn: async ({ userId, options }: { userId: number; options: { dryRun?: boolean; confirm?: string } }) => {
      const token = await getAccessTokenSilently();
      return adminApi.purgeUserData(userId, options, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

// ─── Image Search Query ───

export function useImageSearch(params: {
  searchTerm?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  siteId?: string;
  descriptorId?: number;
  collectionIds?: number[];
  page: number;
  size: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
}) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['images', 'search', params],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/image/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          searchTerm: params.searchTerm || undefined,
          startDate: params.startDate || undefined,
          endDate: params.endDate || undefined,
          siteId: params.siteId || undefined,
          descriptorId: params.descriptorId || undefined,
          collectionIds: params.collectionIds && params.collectionIds.length > 0 ? params.collectionIds : undefined,
          page: params.page,
          size: params.size,
          sortField: params.sortField,
          sortDirection: params.sortDirection,
        }),
      });
      if (!response.ok) throw new Error('Failed to fetch images');
      return response.json();
    },
    enabled: isAuthenticated,
  });
}

// ─── Image Detail Query ───

export function useImage(id: string | undefined) {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['images', id],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/image/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch image');
      return response.json();
    },
    enabled: isAuthenticated && !!id,
  });
}

// ─── All Collections (for ViewTable filter) ───

export function useAllCollectionsForFilter() {
  const { getAccessTokenSilently, isAuthenticated } = useToken();
  return useQuery({
    queryKey: ['collections', 'all-for-filter'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const [myCollections, accessibleCollections] = await Promise.all([
        collectionApi.getMyCollections(token),
        collectionApi.getAccessible(token),
      ]);
      const allCollections = [...myCollections, ...accessibleCollections];
      const uniqueCollections = Array.from(
        new Map(allCollections.map((c: any) => [c.id, c])).values()
      );
      return uniqueCollections;
    },
    enabled: isAuthenticated,
  });
}
